import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Box, Alert, Slide } from '@mui/material';

// Lightweight toast for real-time ("someone else changed this") notifications.
// Stacks in the top-right, just under the sticky navbar. Each auto-dismisses.
interface Toast {
  id: number;
  message: string;
}

interface RealtimeToastValue {
  notify: (message: string) => void;
}

const RealtimeToastContext = createContext<RealtimeToastValue>({ notify: () => {} });

export const useRealtimeToast = () => useContext(RealtimeToastContext);

const AUTO_DISMISS_MS = 6000;

export const RealtimeToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((message: string) => {
    if (!message) return;
    const id = (idRef.current += 1);
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => remove(id), AUTO_DISMISS_MS);
  }, [remove]);

  return (
    <RealtimeToastContext.Provider value={{ notify }}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          top: 52, // clears the ~40px sticky navbar
          right: 16,
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 380,
          pointerEvents: 'none', // let clicks through the empty container; alerts re-enable below
        }}
      >
        {toasts.map((t) => (
          <Slide key={t.id} direction="left" in mountOnEnter unmountOnExit>
            <Alert
              severity="info"
              variant="filled"
              onClose={() => remove(t.id)}
              sx={{ pointerEvents: 'auto', boxShadow: 3 }}
            >
              {t.message}
            </Alert>
          </Slide>
        ))}
      </Box>
    </RealtimeToastContext.Provider>
  );
};
